import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "react-toastify";

import ENV from "config/Env";
import { typedQueryClient, useTypedQuery } from "hooks";
import { APIKey, APIKeyLog, API_KEY_STATUS } from "models";
import { CreateApiReadingKey } from "modules/ApiKeys/components";
import { useKeysFilters } from "modules/ApiKeys/hooks";
import { ApiKeys } from "modules/ApiKeys/queryKeys";
import { ApiReadingKeyRevealPayload } from "modules/ApiKeys/types";
import ApiInstance from "util/Api";

const fallbackApoReadingKeysSorting = (a: APIKey, b: APIKey) => {
  if (a.status === API_KEY_STATUS.Active && b.status !== API_KEY_STATUS.Active) return -1;
  if (a.status !== API_KEY_STATUS.Active && b.status === API_KEY_STATUS.Active) return 1;
  if (a.status === b.status) {
    if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
    if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
  }

  return 0;
};

const sortApiReadingKeys = (apiReadingKeys: APIKey[]) =>
  [...apiReadingKeys].sort((a, b) => {
    if (!a.company_name || !b.company_name) return fallbackApoReadingKeysSorting(a, b);
    if (a.company_name.toLowerCase() === b.company_name.toLowerCase()) return fallbackApoReadingKeysSorting(a, b);
    if (a.company_name.toLowerCase() < b.company_name.toLowerCase()) return -1;
    if (a.company_name.toLowerCase() > b.company_name.toLowerCase()) return 1;

    return 0;
  });

export const useApiReadingKeySearch = () => {
  const filters = useKeysFilters();

  const query = useTypedQuery({
    queryKey: ApiKeys.tokens,
    queryFn: async () => {
      const response = await ApiInstance.post("/api_key/search", filters, ENV.systemBaseUrl);
      return sortApiReadingKeys(response.data.api_keys ? (response.data.api_keys as APIKey[]) : []);
    }
  });

  useEffect(() => {
    query.refetch();
  }, [filters]);

  return query;
};

export const useApiKeyCreate = () =>
  useMutation({
    mutationFn: async (payload: CreateApiReadingKey) => {
      const response = await ApiInstance.post("/api_key/create", payload, ENV.systemBaseUrl);
      return response.data;
    },
    onMutate: (createdApiReadingKey: CreateApiReadingKey) => {
      const previousApiReadingKeys = typedQueryClient.getQueryData(ApiKeys.tokens) || [];

      typedQueryClient.setQueryData(ApiKeys.tokens, [...previousApiReadingKeys, createdApiReadingKey as APIKey]);
      return { previousApiReadingKeys };
    },
    onSuccess: () => typedQueryClient.invalidateQueries(ApiKeys.tokens),
    onError: (e, _, context) => {
      toast.error(e.message);
      if (context?.previousApiReadingKeys) typedQueryClient.setQueryData(ApiKeys.tokens, context.previousApiReadingKeys);
    }
  });

export const useApiReadingKeysRevoke = () =>
  useMutation({
    mutationFn: async (ID: number) => {
      const response = await ApiInstance.post("/api_key/revoke", { ID }, ENV.systemBaseUrl);
      return response.data;
    },
    onMutate: (ID: number) => {
      const previousApiReadingKeys = typedQueryClient.getQueryData(ApiKeys.tokens) || [];
      const appIdx = previousApiReadingKeys.findIndex(token => token.id === ID);
      if (appIdx === -1) return;

      typedQueryClient.setQueryData(ApiKeys.tokens, previousApiReadingKeys.with(appIdx, { ...previousApiReadingKeys[appIdx], status: API_KEY_STATUS.Revoked }));
      return { previousApiReadingKeys };
    },
    onError: (e, _, context) => {
      toast.error(e.message);
      if (context?.previousApiReadingKeys) typedQueryClient.setQueryData(ApiKeys.tokens, context.previousApiReadingKeys);
    }
  });

export const useApiReadingKeysReveal = (id: number | null) =>
  useMutation({
    mutationFn: async (payload: ApiReadingKeyRevealPayload) => {
      const response = await ApiInstance.post("/api_key/key", payload, ENV.systemBaseUrl);
      return response.data;
    },
    onSuccess: (response: APIKey) => {
      if (!id) return;
      const tokens = typedQueryClient.getQueryData(ApiKeys.tokens);
      if (!tokens) return;

      const appIdx = tokens.findIndex(token => token.id === id);
      if (appIdx === -1) return;

      typedQueryClient.setQueryData(ApiKeys.tokens, tokens.with(appIdx, { ...tokens[appIdx], ...response }));
    },
    onError: e => toast.error(e.message)
  });

export const useApiReadingKeysLogs = (requestLogsID: number | null) => {
  const query = useTypedQuery({
    queryKey: ApiKeys.logs,
    queryFn: async () => {
      const response = await ApiInstance.post("/api_key/logs", { ID: requestLogsID }, ENV.systemBaseUrl);
      return !response.data.logs ? [] : (response.data.logs as APIKeyLog[]);
    },
    enabled: Boolean(requestLogsID)
  });

  useEffect(() => {
    if (requestLogsID) query.refetch();
  }, [requestLogsID]);

  return query;
};
